import { Link } from 'react-router-dom'
import PublicLayout from '@/components/layout/PublicLayout'

export default function OurStory() {
  return (
    <PublicLayout>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-3xl space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <svg className="w-12 h-12 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Why SSDL Exists</h1>
            <p className="text-[var(--text-secondary)] mt-2">The story behind this app</p>
          </div>

          {/* The Problem */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">It Started with a Problem</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              I built SSDL because I needed it myself. I've been a struggling alcoholic for 30 years. Like a lot of people, my drinking crept up over the years without me really noticing. There was no single dramatic moment — just a slow drift from "a couple on the weekend" to "most nights" to "more than I'd want to admit to my doctor."
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              The turning point wasn't hitting rock bottom. It was the quiet realisation that I genuinely didn't know how much I was drinking. I'd tell myself "not that much" but I had no data to back that up. When I finally looked at the numbers honestly, they were worse than I'd thought.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              I've been tracking my drinking using a version of this app for the last 6 years. It hasn't magically fixed anything — but it's given me something I never had before: honesty with myself. Seeing the real numbers, week after week, month after month, has been more powerful than any lecture or self-help book.
            </p>
          </div>

          {/* Why Not Existing Apps */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">Why Not Use Something That Already Existed?</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              I tried. Every drink tracking app I found wanted my name, my email, sometimes even my phone number. One wanted me to connect with "accountability partners." Another had a social feed. For something as deeply personal as tracking how much you drink, the last thing you want is an account tied to your identity.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              I'm a software engineer. I knew I could build something better — something that didn't need to know who I was, couldn't sell my data because it couldn't read it, and just showed me the numbers without any judgement or social pressure.
            </p>
          </div>

          {/* The Technical Ability */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">Building It Right</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              Having the technical skills to build this felt like a responsibility. Most people who struggle with drinking don't have access to good tools — the ones that exist are either too clinical, too invasive, or too expensive. I had the ability to make something genuinely useful and genuinely private, so I did.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              The privacy-first architecture isn't a marketing angle — it's there because I wouldn't have used the app myself otherwise. When you're logging drinks you're not proud of, you need to know that data is <em>yours</em>. Not your insurance company's. Not an advertiser's. Not even mine. That's why SSDL uses end-to-end encryption: I literally cannot read your data, even if I wanted to.
            </p>
          </div>

          {/* The Goal */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">The Goal: Reduce Harm</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              Alcohol is one of the leading causes of preventable death worldwide. In Australia alone, around 6,000 people die from alcohol-related causes every year. Globally, it's over 3 million. These aren't just statistics — they're parents, partners, friends, colleagues.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              SSDL won't fix that. But I believe that awareness is the first step toward change. Most people don't need to be told drinking is bad — they need a private, honest way to see what they're actually doing. When you see your BAC hit 0.15% on a Tuesday night, or your GP report shows you're averaging 40 standard drinks a week, the numbers speak for themselves.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              That's why SSDL is free and always will be. Putting a paywall on a harm reduction tool would defeat the purpose. The people who need this most are often the ones least likely to pay for an app — and they shouldn't have to.
            </p>
          </div>

          {/* What SSDL Is Not */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">What SSDL Is Not</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              SSDL is not a treatment program. It's not a replacement for professional help. It's not going to tell you to stop drinking or shame you when you don't. It's a mirror — it shows you the data and lets you decide what to do with it.
            </p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              If the data worries you, the app makes it easy to take the next step: download a GP report, find region-specific helplines, or just take a longer look at your sobriety streaks and think about what you want them to look like.
            </p>
          </div>

          {/* CTA */}
          <div className="card border-[var(--accent-teal)] text-center">
            <p className="text-[var(--text-primary)] font-medium mb-4">
              If this sounds like something you or someone you know could use — give it a try. It takes 5 seconds and costs nothing.
            </p>
            <Link to="/#get-started" className="btn btn-primary px-8">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
